import { useMutation, useQuery, useQueryClient } from "react-query";
import { z } from "zod";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { sureApiCall } from "src/api/axiosClient/sureApi";
import { TemplateDetailSchema } from "./useActionPlanTemplate";
import { ActionPlanTemplateSchema } from "./useActionPlanTemplates";

const PublishedTemplatesSchema = z.object({
    "hydra:member": ActionPlanTemplateSchema.array(),
});
const SsqVersionsSchema = z.object({
    versions: z.array(z.object({ id: z.number().int().positive(), status: z.number().int() })),
});

export const useTemplatePublicationContext = (id: string) =>
    useQuery({
        queryKey: ["templatePublication", id],
        retry: false,
        refetchOnWindowFocus: false,
        cacheTime: 0,
        queryFn: async () => {
            const detail = await loggedInApiCall.get(`/api/action-plan-templates/${id}`);
            const template = TemplateDetailSchema.parse(detail.data);
            const [publishedResponse, ssqResponse] = await Promise.all([
                loggedInApiCall.get("/api/action-plan-templates", {
                    params: {
                        ssqId: template.ssqId,
                        ssqVersionId: template.ssqVersionId,
                        "sector.id": template.sectorId,
                        status: "published",
                    },
                }),
                sureApiCall.get(`/api/ssqs/${template.ssqId}`),
            ]);
            const existing = PublishedTemplatesSchema.parse(publishedResponse.data)["hydra:member"][0] ?? null;
            const ssq = SsqVersionsSchema.parse(ssqResponse.data);
            const version = ssq.versions.find((candidate) => candidate.id === template.ssqVersionId);

            return {
                template,
                existing,
                ssqVersionAvailable: version !== undefined,
                ssqVersionStatus: version?.status,
            };
        },
    });

export const usePublishActionPlanTemplate = (id: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (replaceTemplateId: string | null) => {
            const response = await loggedInApiCall.post(
                `/api/action-plan-templates/${id}/publish`,
                { replaceTemplateId },
                { headers: { "Content-Type": "application/json" } },
            );
            return TemplateDetailSchema.parse(response.data);
        },
        onSuccess: (_template, replaceTemplateId) => {
            void queryClient.invalidateQueries(["actionPlanTemplates"]);
            void queryClient.invalidateQueries(["actionPlanTemplate", id]);
            if (replaceTemplateId !== null) {
                void queryClient.invalidateQueries(["actionPlanTemplate", replaceTemplateId]);
            }
        },
    });
};
